import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Medication, medicationService } from '../../services/medicationService';
import { meetingMedicationService, MeetingMedication } from '../../services/meetingMedicationService';
import { MEDICATION_UNIT_OPTIONS } from '../../constants/medicationUnits';

export interface MedicationSectionHandle {
  save: () => Promise<void>;
}

interface MedicationSectionProps {
  meetingId: number;
  medicationTypes?: Medication[];
  showToggle?: boolean;
  onMedicationsChange?: () => void;
}

// Pending medications use negative ids (not yet persisted to API)
const newTempId = (() => {
  let counter = -1;
  return () => counter--;
})();

export const MedicationSection = forwardRef<MedicationSectionHandle, MedicationSectionProps>(({
  meetingId,
  medicationTypes: propMedicationTypes,
  showToggle = false,
  onMedicationsChange,
}, ref) => {
  const [medicationsEnabled, setMedicationsEnabled] = useState(true);
  const [medications, setMedications] = useState<MeetingMedication[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [medicationTypes, setMedicationTypes] = useState<Medication[]>(propMedicationTypes || []);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({
    medication: '',
    medication_type_id: 0,
    dosage_amount: '',
    dosage_unit: '',
    freq_amount: '',
    freq_period: 'Day' as 'Day' | 'Week' | 'Month',
    duration_in_days: '',
    comments: '',
  });
  const [error, setError] = useState('');
  const [medicationSearch, setMedicationSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Keep latest state accessible in the imperative save callback
  const medicationsRef = useRef(medications);
  const pendingDeleteIdsRef = useRef(pendingDeleteIds);
  useEffect(() => { medicationsRef.current = medications; }, [medications]);
  useEffect(() => { pendingDeleteIdsRef.current = pendingDeleteIds; }, [pendingDeleteIds]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      const current = medicationsRef.current;
      const deletes = pendingDeleteIdsRef.current;

      const deletePromises = deletes.map(id => meetingMedicationService.deleteMeetingMedication(id));
      const pending = current.filter(m => m.id < 0);
      const createPromises = pending.map(m =>
        meetingMedicationService.createMeetingMedication(meetingId, {
          meeting_id: meetingId,
          medication_type_id: m.medication_type_id,
          dosage_amount: m.dosage_amount,
          dosage_unit: m.dosage_unit,
          freq_amount: m.freq_amount,
          freq_period: m.freq_period,
          duration_in_days: m.duration_in_days,
          comments: m.comments,
        })
      );

      const created = await Promise.all([...deletePromises, ...createPromises]);
      const savedMeds = created.filter((r): r is MeetingMedication => r !== undefined && 'id' in r);

      // Replace pending items with server-returned items, keep already-saved ones
      setMedications(prev => {
        const kept = prev.filter(m => m.id > 0);
        return [...kept, ...savedMeds];
      });
      setPendingDeleteIds([]);
    },
  }));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meds, types] = await Promise.all([
          meetingMedicationService.getMeetingMedications(meetingId),
          propMedicationTypes ? Promise.resolve(propMedicationTypes) : medicationService.getMedications(),
        ]);
        setMedications(meds);
        if (!propMedicationTypes) setMedicationTypes(types);
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propMedicationTypes) setMedicationTypes(propMedicationTypes);
  }, [propMedicationTypes]);

  const validate = (): boolean => {
    if (!form.medication) {
      setError('Please select a medication');
      return false;
    }
    const dosage = parseFloat(form.dosage_amount);
    if (isNaN(dosage) || form.dosage_amount === '' || dosage < 0) {
      setError('Dosage must be a valid number ≥ 0');
      return false;
    }
    const freq = parseFloat(form.freq_amount);
    if (isNaN(freq) || form.freq_amount === '' || freq < 0) {
      setError('Amount must be a valid number ≥ 0');
      return false;
    }
    const dur = parseInt(form.duration_in_days, 10);
    if (isNaN(dur) || form.duration_in_days === '' || dur <= 0 || form.duration_in_days !== dur.toString()) {
      setError('Duration must be a positive integer');
      return false;
    }
    if (!form.dosage_unit) {
      setError('Please select a unit');
      return false;
    }
    setError('');
    return true;
  };

  const clearErrorIfFixed = () => {
    if (error) validate();
  };

  const handleAdd = () => {
    if (!validate()) return;

    const tempMed: MeetingMedication = {
      id: newTempId(),
      meeting_id: meetingId,
      medication_type_id: form.medication_type_id || null,
      dosage_amount: parseFloat(form.dosage_amount),
      dosage_unit: form.dosage_unit,
      freq_amount: parseFloat(form.freq_amount),
      freq_period: form.freq_period,
      duration_in_days: parseInt(form.duration_in_days, 10),
      // Only the titration Treatment tab records these two.
      dosage_instructions: null,
      quantity: null,
      comments: form.comments || null,
      created_at: '',
      updated_at: '',
    };

    setMedications(prev => [...prev, tempMed]);
    setForm({
      medication: '',
      medication_type_id: 0,
      dosage_amount: '',
      dosage_unit: '',
      freq_amount: '',
      freq_period: 'Day',
      duration_in_days: '',
      comments: '',
    });
    setError('');
    onMedicationsChange?.();
  };

  const handleRemove = (id: number) => {
    if (id > 0) {
      // Saved in DB — mark for deletion on save
      setPendingDeleteIds(prev => [...prev, id]);
    }
    setMedications(prev => prev.filter(m => m.id !== id));
    onMedicationsChange?.();
  };

  const getFiltered = () => {
    if (!medicationSearch) return medicationTypes.slice(0, 50);
    const q = medicationSearch.toLowerCase();
    return medicationTypes.filter(m => m.name.toLowerCase().includes(q)).slice(0, 50);
  };

  const handleSelectType = (med: Medication) => {
    setForm(prev => ({
      ...prev,
      medication: med.name,
      medication_type_id: med.id,
      dosage_amount: med.dosage_amount?.toString() ?? '',
      dosage_unit: med.dosage_unit ?? '',
      freq_amount: med.freq_amount?.toString() ?? '',
      freq_period: (med.freq_period as 'Day' | 'Week' | 'Month') || 'Day',
      duration_in_days: med.duration_in_days?.toString() ?? '',
    }));
    setMedicationSearch('');
    setShowDropdown(false);
  };

  if (isLoading) return <div className="p-4 text-sm text-gray-500">Loading medications...</div>;

  return (
    <div className="p-4 bg-[#EBF5F5] rounded-lg">
      {showToggle && (
        <div className="flex items-center justify-start gap-2 mb-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={medicationsEnabled}
              onChange={(e) => setMedicationsEnabled(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-400 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#3a7380]"></div>
          </label>
          <label className="text-xs font-medium text-gray-700">Recommended medications</label>
        </div>
      )}

      {medicationsEnabled && (
        <>
          {/* Form */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {/* Medication autocomplete */}
            <div className="relative">
              <input
                type="text"
                placeholder="Medication"
                value={medicationSearch || form.medication}
                onChange={(e) => {
                  setMedicationSearch(e.target.value);
                  setForm(prev => ({ ...prev, medication: e.target.value }));
                }}
                onFocus={() => setShowDropdown(true)}
                className="w-full px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
              />
              {showDropdown && medicationSearch && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => { setShowDropdown(false); setMedicationSearch(''); }}
                  />
                  <div className="absolute z-20 w-[500px] mt-1 bg-white border border-[#B4D9D4] rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {getFiltered().length > 0 ? (
                      getFiltered().map(med => (
                        <div
                          key={med.id}
                          onClick={() => handleSelectType(med)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-gray-900">{med.name}</div>
                          <div className="text-xs text-gray-600">
                            {med.dosage_amount} {med.dosage_unit} • {med.freq_amount}x/{med.freq_period} • {med.duration_in_days} days
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">No medications found</div>
                    )}
                  </div>
                </>
              )}
            </div>

            <input
              type="number"
              placeholder="Dosage"
              step="0.01"
              value={form.dosage_amount}
              onChange={(e) => { setForm(prev => ({ ...prev, dosage_amount: e.target.value })); clearErrorIfFixed(); }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            <select
              value={form.dosage_unit}
              onChange={(e) => { setForm(prev => ({ ...prev, dosage_unit: e.target.value })); clearErrorIfFixed(); }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            >
              <option value="">Select unit</option>
              {MEDICATION_UNIT_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.label}>{opt.label}</option>
              ))}
            </select>

            <input
              type="number"
              placeholder="Amount"
              step="0.01"
              value={form.freq_amount}
              onChange={(e) => { setForm(prev => ({ ...prev, freq_amount: e.target.value })); clearErrorIfFixed(); }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            <select
              value={form.freq_period}
              onChange={(e) => setForm(prev => ({ ...prev, freq_period: e.target.value as 'Day' | 'Week' | 'Month' }))}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            >
              <option value="Day">Per day</option>
              <option value="Week">Per week</option>
              <option value="Month">Per month</option>
            </select>

            <input
              type="number"
              placeholder="Duration (Days)"
              step="1"
              value={form.duration_in_days}
              onChange={(e) => { setForm(prev => ({ ...prev, duration_in_days: e.target.value })); clearErrorIfFixed(); }}
              className="px-3 py-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
            />

            <button
              onClick={handleAdd}
              className="w-8 h-8 flex items-center justify-center bg-white text-black rounded-lg shadow-sm hover:bg-[#D0E7E5] transition-all font-bold border-0 outline-none text-xl cursor-pointer"
            >
              +
            </button>
          </div>

          <input
            type="text"
            placeholder="Comments"
            value={form.comments}
            onChange={(e) => setForm(prev => ({ ...prev, comments: e.target.value }))}
            className="w-full px-3 py-2 mb-2 border border-[#B4D9D4] rounded-lg bg-[#B5D9D4] focus:outline-none focus:ring-2 focus:ring-[#366A75] focus:border-transparent"
          />

          {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

          {/* Table */}
          {medications.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Dosage</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Unit</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Amount</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Frequency</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Duration (Days)</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Comments</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {medications.map(med => {
                    const type = medicationTypes.find(t => t.id === med.medication_type_id);
                    return (
                      <tr key={med.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{type?.name ?? '-'}</td>
                        <td className="px-3 py-2">{med.dosage_amount ?? '-'}</td>
                        <td className="px-3 py-2">{med.dosage_unit ?? '-'}</td>
                        <td className="px-3 py-2">{med.freq_amount ?? '-'}</td>
                        <td className="px-3 py-2">{med.freq_period ?? '-'}</td>
                        <td className="px-3 py-2">{med.duration_in_days ?? '-'}</td>
                        <td className="px-3 py-2">{med.comments ?? '-'}</td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => handleRemove(med.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
});
